import { type ReactNode, useEffect, useRef } from 'react';

interface DetailDrawerProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  /**
   * Panel width at `md`+. `'default'` keeps the original `max-w-md` — every pre-existing consumer
   * relies on it, so widening globally would silently reshape all of them. `'wide'` is ~75% viewport
   * for drawers that carry a working surface rather than a summary (operator request 2026-08-11: the
   * project detail drawer needs 3/4 page width). Below `md` both are full-width regardless.
   */
  size?: 'default' | 'wide';
}

/**
 * Generic right-side detail drawer (#13136) — generalised from `HelpDrawer`'s slide-out
 * panel pattern (`apps/admin-portal/src/components/HelpDrawer.tsx`), with the help-content
 * specifics (markdown loading, the help index) stripped out. Full-screen sheet below the
 * `md` breakpoint (via `w-full`); at `md`+ a `max-w-md` panel, or ~75vw when `size='wide'`. Escape and backdrop
 * click both close; focus moves to the close button on open and is trapped within the
 * drawer while open (Tab from the last focusable element wraps to the first, and vice
 * versa with Shift+Tab).
 */
export function DetailDrawer({ open, onClose, title, children, footer, size = 'default' }: DetailDrawerProps) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLElement>(null);

  // Focus-on-open only — deliberately keyed on `open` alone (not `onClose`). A caller
  // that renders a form inside the drawer (e.g. AddDomainDrawer) naturally passes a new
  // `onClose` closure on every keystroke-driven re-render; keying this effect on `onClose`
  // too would re-run it on every keystroke and steal focus back to the close button after
  // the first character (caught by DomainsRegistryTab.test.tsx's "creates a domain" test).
  useEffect(() => {
    if (!open) return;
    closeRef.current?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key !== 'Tab' || !panelRef.current) return;
      const focusable = panelRef.current.querySelectorAll<HTMLElement>(
        'button, a[href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <>
      <button
        type="button"
        className="fixed inset-0 z-40 bg-black/30"
        aria-label={`Close ${title}`}
        onClick={onClose}
      />
      <aside
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={`fixed right-0 top-0 z-50 flex h-full w-full flex-col bg-white shadow-lg animate-in slide-in-from-right duration-200 ${size === 'wide' ? 'md:max-w-[75vw]' : 'max-w-md'}`}
      >
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
          <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
          <button ref={closeRef} type="button" onClick={onClose} aria-label="Close" className="text-gray-400 hover:text-gray-600">
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-5">{children}</div>
        {footer && <div className="border-t border-gray-200 p-4 flex justify-end gap-3">{footer}</div>}
      </aside>
    </>
  );
}
